import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  DescriptionOutlined as DocumentIcon,
  Visibility as PreviewIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { getDocumentsByUnitNo, getDocumentVersionForPreview } from '../api';
import { DocumentPreviewUI } from '../DocumentPreview';
import { UploadDocument } from './UploadDocument';
import { DOCUMENT_TYPES } from './Constants';

const StaticDocumentCard = ({ documentType, documents, onPreview, onUpload, isNADisabled, onNAToggle, setDocumentErrors }) => {
  const hasDocuments = documents && documents.length > 0;
  const documentCount = documents[0]?.previous_versions?.length ? documents[0].previous_versions.length + 1 : hasDocuments ? 1 : 0;
  const isVisible = documentType.alwaysVisible || hasDocuments;

  useEffect(() => {
    if (isVisible) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentType.id]: !isNADisabled && !hasDocuments ? `At least one ${documentType.title} document is required.` : '',
      }));
    }
  }, [isNADisabled, hasDocuments, documentType.id, documentType.title, setDocumentErrors, isVisible]);

  return (
    <Grid item xs={12} sm={6} md={6} key={documentType.id}>
      <Card
        variant="outlined"
        sx={{
          border: 2,
          borderColor: hasDocuments || isNADisabled ? 'success.main' : 'error.main',
          backgroundColor: hasDocuments || isNADisabled ? 'rgb(225 255 227)' : 'rgb(255 233 233)',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
          minWidth: 150,
          opacity: isNADisabled ? 0.6 : 1,
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
              {hasDocuments || isNADisabled ? (
                <CheckIcon sx={{ color: 'success.main', mr: 1.5, fontSize: 20 }} />
              ) : (
                <ErrorIcon sx={{ color: 'error.main', mr: 1.5, fontSize: 20 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    noWrap
                    title={documentType.title}
                  >
                    {documentType.title}
                  </Typography>
                  {/* The switch shows when there is nothing uploaded — and also whenever the
                      flag is ON, so a stale N/A on a type that later got documents can
                      always be untoggled instead of being stuck out of reach. */}
                  {(documentCount < 1 || isNADisabled) && (
                    <FormControlLabel
                      control={<Switch checked={isNADisabled} onChange={() => onNAToggle(documentType.id)} size="small" />}
                      label="N/A"
                      labelPlacement="end"
                      sx={{
                        m: 0,
                        '& .MuiTypography-root': { fontSize: '0.75rem' },
                        pointerEvents: 'auto'
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {documentType.description}{' '}
                  {documentType.autoDelete && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                    >
                      ⚠️ Auto-deletes in {documentType.deleteDays} days
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.5,
              mt: 1,
            }}>
              <Tooltip title={`Preview ${documentType.title} Documents`}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onPreview(documentType, documents)}
                  // N/A means "none expected" — it must never lock files that DO exist.
                  // (Set All as N/A used to flag doc-holding types too, and with the switch
                  // hidden once a document exists, preview was stuck disabled forever.)
                  disabled={documentType.alwaysVisible && isNADisabled && !hasDocuments}
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'primary.light' },
                  }}
                >
                  <Badge
                    badgeContent={documentCount}
                    color={hasDocuments ? 'success' : 'error'}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    <PreviewIcon fontSize="small" sx={{ color: hasDocuments || isNADisabled ? 'primary.main' : 'error.main' }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title={`Upload ${documentType.title}`}>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onUpload(documentType)}
                  disabled={isNADisabled}
                  sx={{
                    border: 1,
                    borderColor: 'secondary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'secondary.light' },
                  }}
                >
                  <UploadIcon fontSize="small" sx={{ color: hasDocuments || isNADisabled ? 'secondary.main' : 'error.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const DocumentTypePreviewDialog = ({ open, onClose, documentType, documents, onUpload, propertyId, buildingName, unitNo, isNADisabled }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showLatest, setShowLatest] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Check if preview/download should be disabled. N/A never locks documents that exist.
  const isPreviewDisabled = documentType?.alwaysVisible && isNADisabled && !(documents && documents.length > 0);

  useEffect(() => {
    if (open && documents && documents.length > 0) {
      setSelectedDocument(documents[0]);
      setShowLatest(true);
      setPreviewError(null);
    } else {
      setSelectedDocument(null);
      setShowLatest(true);
      setPreviewError(null);
    }
  }, [open, documents]);

  const handleDocumentSelect = async (document, isLatest = false) => {
    if (isPreviewDisabled) return;
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      if (isLatest || !document.version_number) {
        if (document.signedUrl && document.signedUrl !== 'upload_requested') {
          setSelectedDocument(document);
          setShowLatest(isLatest);
        } else {
          throw new Error('Latest document preview not available');
        }
      } else {
        const versionData = await getDocumentVersionForPreview(
          propertyId,
          buildingName,
          unitNo,
          documentType.document_type,
          document.version_number
        );

        if (versionData && versionData.signedUrl) {
          setSelectedDocument({
            ...document,
            signedUrl: versionData.signedUrl,
          });
          setShowLatest(false);
        } else {
          throw new Error('Failed to get preview URL for this version');
        }
      }
    } catch (error) {
      setPreviewError('Failed to load document preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async (document) => {
    if (isPreviewDisabled) return;
    if (typeof window === 'undefined') return;

    try {
      let downloadUrl = document.signedUrl;
      let fileName = document.document_type || `${documentType?.title}_${document.id || document.version_number}`;

      if (document.version_number && !document.signedUrl) {
        const versionData = await getDocumentVersionForPreview(
          propertyId,
          buildingName,
          unitNo,
          documentType.document_type,
          document.version_number
        );

        if (versionData && versionData.signedUrl) {
          downloadUrl = versionData.signedUrl;
        } else {
          throw new Error('Failed to get download URL for this version');
        }
      }

      if (downloadUrl && downloadUrl !== 'upload_requested') {
        const link = window.document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
      } else {
        throw new Error('Document not available for download');
      }
    } catch (err) {
      console.error(`Error downloading document ${document.document_type}:`, err);
      Swal.fire('Error', 'Failed to download document. Please try again.', 'error');
    }
  };

  const handleDownloadAll = async () => {
    if (isPreviewDisabled) return;
    try {
      if (documents[0] && documents[0].url !== 'upload_requested' && documents[0].signedUrl) {
        await handleDownload(documents[0]);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      if (documents[0]?.previous_versions) {
        for (const version of documents[0].previous_versions) {
          await handleDownload(version);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    } catch (error) {
      console.error('Error downloading all documents:', error);
      Swal.fire('Error', 'Some documents could not be downloaded. Please try again.', 'error');
    }
  };

  const handleUploadClick = () => {
    if (isNADisabled) return;
    onUpload(documentType);
    onClose();
  };

  const handleToggleLatest = () => {
    if (isPreviewDisabled) return;
    if (documents && documents.length > 0) {
      if (!showLatest) {
        handleDocumentSelect(documents[0], true);
      } else {
        setShowLatest(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {documentType?.title || 'Documents'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {documents && documents.length > 0 && (
            <>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FeatherIcon icon="download" size="16" />}
                onClick={handleDownloadAll}
                disabled={isPreviewDisabled}
              >
                Download All
              </Button>
            </>
          )}
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<FeatherIcon icon="upload" size="16" />}
            onClick={handleUploadClick}
            disabled={isNADisabled}
          >
            Upload New
          </Button>
          <IconButton onClick={onClose} size="small">
            <FeatherIcon icon="x" size="16" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {documents && documents.length > 0 ? (
          <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
              {loadingPreview ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <CircularProgress />
                  <Typography variant="body2" ml={2}>Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Alert severity="error">{previewError}</Alert>
                </Box>
              ) : selectedDocument && selectedDocument.signedUrl && selectedDocument.signedUrl !== 'upload_requested' ? (
                <DocumentPreviewUI
                  documentUrl={selectedDocument.url}
                  previewURL={selectedDocument.signedUrl}
                  width="100%"
                  height="60vh"
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Typography color="text.secondary">Document preview not available.</Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Document Versions
                </Typography>
                <FormControlLabel
                  control={<Switch checked={showLatest} onChange={handleToggleLatest} size="small" disabled={isPreviewDisabled} />}
                  label="Show Latest"
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
              </Box>
              <Grid container spacing={1}>
                {documents[0] && (
                  <Grid item xs={6} sm={6} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: documents[0].url !== 'upload_requested' ? 'success.main' : 'error.main',
                        backgroundColor: selectedDocument === documents[0] && showLatest
                          ? 'primary.light'
                          : documents[0].url !== 'upload_requested'
                            ? 'rgb(225 255 227)'
                            : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: isPreviewDisabled ? 'not-allowed' : 'pointer',
                        opacity: isPreviewDisabled ? 0.6 : 1,
                      }}
                      onClick={() => !isPreviewDisabled && handleDocumentSelect(documents[0], true)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {documents[0].url !== 'upload_requested' ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <DocumentIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                noWrap
                                title="Latest Version"
                              >
                                Latest
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {documents[0].comments ? documents[0].comments : 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded: {documents.updated_at ? new Date(documents.createdAt).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentSelect(documents[0], true);
                                }}
                                disabled={isPreviewDisabled || documents[0].url === 'upload_requested'}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(documents[0]);
                                }}
                                disabled={isPreviewDisabled || documents[0].url === 'upload_requested'}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {documents[0]?.previous_versions?.map((version) => (
                  <Grid item xs={6} sm={6} md={4} key={version.version_number}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: version.url !== 'upload_requested' ? 'success.main' : 'error.main',
                        backgroundColor: selectedDocument?.version_number === version.version_number && !showLatest
                          ? 'primary.light'
                          : version.url !== 'upload_requested'
                            ? 'rgb(225 255 227)'
                            : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: isPreviewDisabled ? 'not-allowed' : 'pointer',
                        opacity: isPreviewDisabled ? 0.6 : 1,
                      }}
                      onClick={() => !isPreviewDisabled && handleDocumentSelect(version, false)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {version.url !== 'upload_requested' ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <DocumentIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={version.comments || 'No comment available'}>
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  noWrap
                                  title={`Version ${version.version_number}`}
                                >
                                  V{version.version_number}
                                </Typography>
                              </Tooltip>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {version.comments ? version.comments : 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded: {version.updated_at ? new Date(version.updated_at).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentSelect(version, false);
                                }}
                                disabled={isPreviewDisabled}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(version);
                                }}
                                disabled={isPreviewDisabled || version.url === 'upload_requested'}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, height: '70vh' }}>
            <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {documentType?.title} documents found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Upload documents to get started
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FeatherIcon icon="upload" size="16" />}
              onClick={handleUploadClick}
              disabled={isNADisabled}
            >
              Upload First Document
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Documents = ({ propertyId, buildingName, unitNo, fetchDocuments, onUpload, documentsNA, setDocumentsNA, setDocumentErrors }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const getDocumentsByType = (typeId) => {
    return documents.filter(doc => doc.document_type === DOCUMENT_TYPES.find(dt => dt.id === typeId)?.document_type);
  };

  const fetchDocs = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!propertyId || !buildingName || !unitNo) {
        console.warn('Missing required parameters for document fetch', { propertyId, buildingName, unitNo });
        setDocuments([]);
        return;
      }

      const response = await getDocumentsByUnitNo(propertyId, buildingName, unitNo);
      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to fetch documents');
      }

      setDocuments(response.data.documents || []);
      // Update document errors only for visible document types
      const newErrors = {};
      DOCUMENT_TYPES.forEach((docType) => {
        const docsOfType = response.data.documents.filter(
          (doc) => doc.document_type === docType.document_type
        );
        const isVisible = docType.alwaysVisible || docsOfType.length > 0;
        newErrors[docType.id] =
          isVisible && !documentsNA[docType.id] && docsOfType.length === 0
            ? `At least one ${docType.title} document is required.`
            : '';
      });
      setDocumentErrors(newErrors);
    } catch (err) {
      setDocuments([]);
      setDocumentErrors(
        DOCUMENT_TYPES.reduce((acc, docType) => ({
          ...acc,
          [docType.id]: docType.alwaysVisible ? 'Error fetching documents.' : '',
        }), {})
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [propertyId, buildingName, unitNo, documentsNA]);

  useEffect(() => {
    if (fetchDocuments) {
      fetchDocuments.current = fetchDocs;
    }
  }, [fetchDocuments]);

  const handlePreviewOpen = (documentType, documentsOfType) => {
    // N/A blocks the preview only when there is truly nothing to show — files that exist
    // must always open (this guard silently ate the click even with the button enabled).
    if (documentType.alwaysVisible && documentsNA[documentType.id] && !(documentsOfType && documentsOfType.length)) return;
    setSelectedDocumentType(documentType);
    setSelectedDocuments(documentsOfType || []);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setSelectedDocumentType(null);
    setSelectedDocuments([]);
  };

  const handleUpload = (documentType) => {
    if (documentsNA[documentType.id]) return;
    if (onUpload) {
      onUpload(documentType);
    }
    setPreviewOpen(false);
  };

  const handleNAToggle = (documentTypeId) => {
    const newNAState = { ...documentsNA, [documentTypeId]: !documentsNA[documentTypeId] };
    setDocumentsNA(newNAState);
    // Update document errors for visible document types
    const docsOfType = getDocumentsByType(documentTypeId);
    const isVisible = DOCUMENT_TYPES.find(dt => dt.id === documentTypeId).alwaysVisible || docsOfType.length > 0;
    setDocumentErrors((prev) => ({
      ...prev,
      [documentTypeId]: isVisible && !newNAState[documentTypeId] && docsOfType.length === 0
        ? `At least one ${DOCUMENT_TYPES.find(dt => dt.id === documentTypeId).title} document is required.`
        : '',
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" ml={2}>
          Loading documents...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="warning" sx={{ my: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 0, pt: 0 }}>
        {DOCUMENT_TYPES.map((documentType) => {
          const documentsOfType = getDocumentsByType(documentType.id);
          const hasDocuments = documentsOfType && documentsOfType.length > 0;

          if (documentType.alwaysVisible || hasDocuments) {
            return (
              <StaticDocumentCard
                key={documentType.id}
                documentType={documentType}
                documents={documentsOfType}
                onPreview={handlePreviewOpen}
                onUpload={handleUpload}
                isNADisabled={documentsNA[documentType.id]}
                onNAToggle={handleNAToggle}
                setDocumentErrors={setDocumentErrors}
              />
            );
          }
          return null;
        })}
      </Grid>
      <DocumentTypePreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        documentType={selectedDocumentType}
        documents={selectedDocuments}
        onUpload={handleUpload}
        propertyId={propertyId}
        buildingName={buildingName}
        unitNo={unitNo}
        isNADisabled={selectedDocumentType ? documentsNA[selectedDocumentType.id] : false}
      />
    </Box>
  );
};

const DocumentsWrapper = ({ propertyId, buildingName, unitNo, setFieldValue, documentErrors, setDocumentErrors, documentsNA, setDocumentsNA }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isAllNA, setIsAllNA] = useState(false);
  const [previousNAState, setPreviousNAState] = useState(null);
  const fetchDocumentsRef = useRef(null);

  const hasRequiredProps = Boolean(propertyId && buildingName && unitNo);
  const hasErrors = Object.values(documentErrors).some((error) => error);

  const getDocumentsByType = (typeId) => {
    return documents.filter(doc => doc.document_type === DOCUMENT_TYPES.find(dt => dt.id === typeId)?.document_type);
  };

  const fetchDocs = async () => {
    try {
      if (!propertyId || !buildingName || !unitNo) {
        setDocuments([]);
        return;
      }
      const response = await getDocumentsByUnitNo(propertyId, buildingName, unitNo);
      console.log({ response })
      if (response && response.success) {
        setDocuments(response.data.documents || []);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setDocuments([]);
    }
  };

  useEffect(() => {
    if (hasRequiredProps) {
      fetchDocs();
    }
  }, [propertyId, buildingName, unitNo]);

  const toggleCollapse = () => setIsOpen((prev) => !prev);

  const handleAddDocument = (documentType = null) => {
    if (documentType && documentsNA[documentType.id]) return;
    setSelectedDocumentType(documentType);
    setUploadDialogOpen(true);
  };

  const handleModalClose = () => {
    setUploadDialogOpen(false);
    setSelectedDocumentType(null);
    if (fetchDocumentsRef.current) {
      fetchDocumentsRef.current();
    }
    fetchDocs();
  };

  const handleDocumentUploaded = () => {
    if (fetchDocumentsRef.current) {
      fetchDocumentsRef.current();
    }
    fetchDocs();
  };

  const handleSetAllNA = () => {
    Swal.fire({
      title: isAllNA ? 'Restore Previous N/A Settings?' : 'Set All Documents as N/A?',
      text: isAllNA
        ? 'This will restore the previous N/A settings for all document types.'
        : 'This will mark all document types as Not Applicable, clearing any associated errors.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: isAllNA ? 'Yes, restore' : 'Yes, set all as N/A',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAllNA && previousNAState) {
          // Restore previous state
          setDocumentsNA(previousNAState);
          setFieldValue('documentsNA', previousNAState);
          setDocumentErrors(
            DOCUMENT_TYPES.reduce((acc, docType) => {
              const docsOfType = getDocumentsByType(docType.id);
              const isVisible = docType.alwaysVisible || docsOfType.length > 0;
              return {
                ...acc,
                [docType.id]: isVisible && !previousNAState[docType.id] && docsOfType.length === 0
                  ? `At least one ${docType.title} document is required.`
                  : '',
              };
            }, {})
          );
          setPreviousNAState(null);
          setIsAllNA(false);
          Swal.fire('Success', 'Previous N/A settings have been restored.', 'success');
        } else {
          // Save current state and set all to N/A — except types that actually hold
          // documents: a file on record can never be "not applicable".
          setPreviousNAState({ ...documentsNA });
          const newNAState = DOCUMENT_TYPES.reduce((acc, docType) => ({
            ...acc,
            [docType.id]: getDocumentsByType(docType.id).length === 0,
          }), {});
          setDocumentsNA(newNAState);
          setFieldValue('documentsNA', newNAState);
          setDocumentErrors(
            DOCUMENT_TYPES.reduce((acc, docType) => ({
              ...acc,
              [docType.id]: '',
            }), {})
          );
          setIsAllNA(true);
          Swal.fire('Success', 'All documents have been marked as N/A.', 'success');
        }
      }
    });
  };

  const handleDownloadAll = async (documentType = null, isPreviewOpen = false) => {
    // N/A blocks downloads only while there is truly nothing on file.
    if (documentType && documentType.alwaysVisible && documentsNA[documentType.id] && getDocumentsByType(documentType.id).length === 0) {
      Swal.fire('Warning', 'Download is disabled when N/A is selected for this document type.', 'warning');
      return;
    }
    if (!documentType && isPreviewOpen) {
      Swal.fire('Warning', 'Please close the preview or select a specific document type to download.', 'warning');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to download ${documentType ? documentType.document_type + ' documents' : 'all documents'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, download',
      cancelButtonText: 'No, cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await getDocumentsByUnitNo(propertyId, buildingName, unitNo);
          if (!response || !response.success) {
            throw new Error(response?.message || 'Failed to fetch documents');
          }

          let documents = response.data.documents || [];
          if (documents.length === 0) {
            Swal.fire('No Documents', 'There are no documents to download.', 'info');
            return;
          }

          // Download All takes every real file. N/A means "none expected" and must never
          // exclude files that exist — a stale flag used to silently drop them here.
          if (documentType) {
            documents = documents.filter(doc => doc.document_type === documentType.document_type);
            if (documents.length === 0) {
              Swal.fire('No Documents', `No ${documentType.document_type} documents found to download.`, 'info');
              return;
            }
          }

          let downloadCount = 0;
          for (const doc of documents) {
            if (doc.url !== 'upload_requested' && doc.signedUrl) {
              try {
                const link = document.createElement('a');
                link.href = doc.signedUrl;
                link.download = doc.document_type || `document_${doc.id}`;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                downloadCount++;
                await new Promise((resolve) => setTimeout(resolve, 1000));
              } catch (err) {
                console.error(`Error downloading document ${doc.document_type}:`, err);
                Swal.fire('Error', `Failed to download ${doc.document_type}.`, 'error');
              }
            }
            if (doc.previous_versions) {
              for (const version of doc.previous_versions) {
                if (version.url !== 'upload_requested') {
                  try {
                    let downloadUrl = version.signedUrl;

                    if (!downloadUrl) {
                      const versionData = await getDocumentVersionForPreview(
                        propertyId,
                        buildingName,
                        unitNo,
                        doc.document_type,
                        version.version_number
                      );
                      downloadUrl = versionData?.signedUrl;
                    }

                    if (downloadUrl) {
                      const link = document.createElement('a');
                      link.href = downloadUrl;
                      link.download = version.document_type || `document_${version.version_number}`;
                      link.target = '_blank';
                      link.rel = 'noopener noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      downloadCount++;
                      await new Promise((resolve) => setTimeout(resolve, 1000));
                    }
                  } catch (err) {
                    console.error(`Error downloading version ${version.version_number}:`, err);
                    Swal.fire('Error', `Failed to download version ${version.version_number}.`, 'error');
                  }
                }
              }
            }
          }

          if (downloadCount === 0) {
            Swal.fire('No Documents', 'No valid documents were available for download.', 'info');
          } else {
            Swal.fire('Success', `${downloadCount} document${downloadCount > 1 ? 's' : ''} downloaded successfully.`, 'success');
          }
        } catch (err) {
          console.error('Error fetching documents for download:', err);
          Swal.fire('Error', 'Failed to fetch documents for download.', 'error');
        }
      }
    });
  };

  if (!hasRequiredProps) {
    return (
      <RCard className="mb-4">
        <RCardHeader className="d-flex justify-content-between align-items-center">
          <div className="p-0">
            Documents
            <Typography variant="caption" component="div">
              Complete the form with property, building, and unit information to view documents.
            </Typography>
          </div>
        </RCardHeader>
      </RCard>
    );
  }

  return (
    <RCard className="mb-4">
      <RCardHeader className="py-3 text-center">
        <RButton
          color={Object.values(documentErrors).some(error => error) ? 'danger' : 'primary'}
          outline
          className="fw-bold px-4 py-2 rounded-pill"
          onClick={toggleCollapse}
        >
          <FeatherIcon icon="file-text" className="me-2" size="16" />
          Documents ({DOCUMENT_TYPES.filter(dt => dt.alwaysVisible).length})
        </RButton>
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            {Object.values(documentErrors).some((error) => error) && (
              <Alert severity="error">
                Please address the following document errors:
                <ul>
                  {Object.entries(documentErrors)
                    .filter(([docTypeId, error]) => {
                      const docType = DOCUMENT_TYPES.find(dt => dt.id === docTypeId);
                      const documentsOfType = getDocumentsByType(docTypeId);
                      const isVisible = docType?.alwaysVisible || documentsOfType.length > 0;
                      return error && isVisible;
                    })
                    .map(([docTypeId, error]) => (
                      <li key={docTypeId}>{error}</li>
                    ))}
                </ul>
              </Alert>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px', gap: 2 }}>
            <RButton
              color="secondary"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={() => handleDownloadAll(null, false)}
            >
              <FeatherIcon icon="download" size="16" />
              Download All
            </RButton>
            <RButton
              color={isAllNA ? "success" : "warning"}
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={handleSetAllNA}
            >
              <FeatherIcon icon="x-circle" size="16" />
              {isAllNA ? 'Restore N/A Settings' : 'Set All as N/A'}
            </RButton>
            <RButton
              color="primary"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={() => handleAddDocument()}
            >
              <FeatherIcon icon="plus" size="16" />
              Add Document
            </RButton>
          </Box>
          <Documents
            propertyId={propertyId}
            buildingName={buildingName}
            unitNo={unitNo}
            fetchDocuments={fetchDocumentsRef}
            onUpload={handleAddDocument}
            documentsNA={documentsNA}
            setDocumentsNA={(newNA) => {
              setDocumentsNA(newNA);
              setFieldValue('documentsNA', newNA);
            }}
            setDocumentErrors={setDocumentErrors}
          />
        </CardContent>
      </RCollapse>
      <UploadDocument
        isOpen={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
        onClose={handleModalClose}
        onDocumentUploaded={handleDocumentUploaded}
        propertyId={propertyId}
        buildingName={buildingName}
        unitNo={unitNo}
        documentType={selectedDocumentType?.document_type || null}
        documents={DOCUMENT_TYPES}
        isNADisabled={selectedDocumentType ? documentsNA[selectedDocumentType.id] : false}
      />
    </RCard>
  );
};

DocumentsWrapper.propTypes = {
  propertyId: PropTypes.string.isRequired,
  buildingName: PropTypes.string.isRequired,
  unitNo: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  documentErrors: PropTypes.object.isRequired,
  setDocumentErrors: PropTypes.func.isRequired,
  documentsNA: PropTypes.object.isRequired,
  setDocumentsNA: PropTypes.func.isRequired,
};

export { DocumentsWrapper as Documents, DocumentTypePreviewDialog };